import { useState, type FormEvent } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import type { Tag } from '@cleartask/shared';
import { ApiClientError } from '../../lib/api.js';
import { ColorField } from '../../components/ui/ColorField.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import { newTagId, useCreateTag, useDeleteTag, useTags, useUpdateTag } from './useTags.js';

/**
 * Tags do usuario (spec 8: Tarefas -> Tags).
 *
 * Este painel e o que faltava para as tags existirem de verdade. O modelo, os
 * schemas e a validacao de posse estavam no repositorio desde a fase 2, e o
 * parser da fase 8 ja reconhecia `#proteção` — mostrava a tag no preview e a
 * descartava, porque nao havia nada capaz de transformar o nome num id.
 * Exatamente a mesma forma do defeito que as listas tinham: infraestrutura
 * completa, nada que a alcancasse. Renomear entrou depois pela mesma razao:
 * `useUpdateTag` existia e nenhum componente o chamava.
 *
 * Cada item e um LINK para `/tarefas?tag=<id>`, e nao um `<div>` com estado
 * local — foi essa a licao das listas. O filtro vindo da URL faz o link
 * funcionar de verdade e torna o endereco compartilhavel.
 *
 * Montado na barra lateral E na aba Mais, porque a barra some abaixo de 768px.
 */
export function TagsPanel() {
  const { data, isPending } = useTags();
  const createTag = useCreateTag();
  const [params] = useSearchParams();

  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [aExcluir, setAExcluir] = useState<Tag | null>(null);

  const tagAtiva = params.get('tag');
  const tags = data?.tags ?? [];

  function submit(event: FormEvent) {
    event.preventDefault();

    const trimmed = name.trim();
    if (trimmed === '') return;

    createTag.mutate(
      { id: newTagId(), name: trimmed },
      {
        // O campo so fecha quando a gravacao passa: com nome repetido o servidor
        // responde 409, e limpar antes esconderia o erro junto com o texto que a
        // pessoa acabou de digitar.
        onSuccess: () => {
          setName('');
          setAdding(false);
        },
      },
    );
  }

  const erro =
    createTag.error instanceof ApiClientError ? createTag.error.message : null;

  return (
    <div className="flex flex-col gap-1">
      {isPending ? <p className="px-3 text-sm text-text-mut">Carregando…</p> : null}

      {!isPending && tags.length === 0 ? (
        <p className="px-3 pb-1 text-sm text-text-mut">
          Nenhuma tag ainda. Crie uma para marcar tarefas com{' '}
          <code className="font-mono">#nome</code> na entrada rápida.
        </p>
      ) : null}

      {tags.map((tag) => (
        <ItemDaTag
          key={tag.id}
          tag={tag}
          ativa={tag.id === tagAtiva}
          onExcluir={() => setAExcluir(tag)}
        />
      ))}

      {erro ? (
        <p role="alert" className="px-3 py-1 text-sm text-overdue">
          {erro}
        </p>
      ) : null}

      {adding ? (
        <form onSubmit={submit} className="px-2 pt-1">
          <label className="sr-only" htmlFor="new-tag">
            Nome da tag
          </label>
          <input
            id="new-tag"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() === '') setAdding(false);
            }}
            placeholder="Nome da tag"
            className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="min-h-touch rounded-sm px-3 text-left text-sm text-brand-blue hover:bg-surface-2"
        >
          + Nova tag
        </button>
      )}

      <DialogoDeExclusao tag={aExcluir} onFechar={() => setAExcluir(null)} />
    </div>
  );
}

/**
 * Uma linha do painel: link de filtro, renomear e excluir.
 *
 * Os dois botoes ficam SEMPRE no DOM, e nao aparecem no hover — revela-los ao
 * passar o mouse os tornaria inalcancaveis por toque e por teclado (spec 10.5).
 */
function ItemDaTag({
  tag,
  ativa,
  onExcluir,
}: {
  tag: Tag;
  ativa: boolean;
  onExcluir: () => void;
}) {
  const updateTag = useUpdateTag();
  const { showToast } = useToast();

  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(tag.name);
  const [cor, setCor] = useState(tag.color);

  function salvar(event: FormEvent) {
    event.preventDefault();

    const limpo = nome.trim();
    const nomeMudou = limpo !== '' && limpo !== tag.name;
    const corMudou = cor !== tag.color;

    // Nada mudou nao vira requisicao: gravar o mesmo valor gastaria uma linha
    // de `audit_log` afirmando uma alteracao que nao houve.
    if (!nomeMudou && !corMudou) {
      setNome(tag.name);
      setCor(tag.color);
      setEditando(false);
      return;
    }

    updateTag.mutate(
      {
        id: tag.id,
        patch: { ...(nomeMudou ? { name: limpo } : {}), ...(corMudou ? { color: cor } : {}) },
      },
      {
        // Renomear NAO reescreve nada nas tarefas: a associacao e por id, e
        // ninguem guarda `#nome` em texto. O que muda de verdade e a entrada
        // rapida — quem continuar digitando o nome antigo vai ouvir que a tag
        // nao existe. Por isso o toast diz o nome novo em vez de so "Salvo".
        //
        // Trocar so a cor nao tem essa consequencia, e a copy acompanha: dizer
        // "Agora é #x" para quem nao mexeu no nome descreveria uma mudanca que
        // nao houve.
        onSuccess: () => {
          showToast({ message: nomeMudou ? `Agora é #${limpo}` : 'Cor da tag salva' });
          setEditando(false);
        },
      },
    );
  }

  const erro = updateTag.error instanceof ApiClientError ? updateTag.error.message : null;

  if (editando) {
    return (
      <form onSubmit={salvar} className="px-2 py-0.5">
        <div className="flex items-center gap-1">
          <label className="sr-only" htmlFor={`rename-tag-${tag.id}`}>
            Novo nome da tag {tag.name}
          </label>
          <input
            id={`rename-tag-${tag.id}`}
            autoFocus
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setNome(tag.name);
                setCor(tag.color);
                setEditando(false);
              }
            }}
            className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
          />

          <ColorField
            id={`color-tag-${tag.id}`}
            label={`Cor da tag ${tag.name}`}
            value={cor}
            onChange={setCor}
          />

          {/*
            Com dois campos e nenhum botao de submit, o HTML desliga a
            submissao implicita por Enter — o formulario ficaria sem NENHUM
            jeito de salvar por toque, que e o acesso do mobile (spec 10.5).
          */}
          <button
            type="submit"
            aria-label={`Salvar a tag ${tag.name}`}
            className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-brand-blue hover:bg-surface-2"
          >
            ✓
          </button>
        </div>
        {erro ? (
          <p role="alert" className="pt-1 text-sm text-overdue">
            {erro}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <div className="flex items-center">
      <NavLink
        to={`/tarefas?tag=${tag.id}`}
        className={[
          'flex min-h-touch flex-1 items-center gap-2 rounded-sm px-3 text-sm transition-colors duration-150',
          ativa ? 'bg-brand-blue-soft font-medium text-brand-blue' : 'hover:bg-surface-2',
        ].join(' ')}
      >
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: tag.color }}
        />
        <span className="truncate">#{tag.name}</span>
      </NavLink>

      <button
        type="button"
        onClick={() => setEditando(true)}
        aria-label={`Renomear a tag ${tag.name}`}
        className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2 hover:text-text"
      >
        ✎
      </button>

      <button
        type="button"
        onClick={onExcluir}
        aria-label={`Excluir a tag ${tag.name}`}
        className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2 hover:text-overdue"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Confirmacao de exclusao.
 *
 * **E aqui que a excecao a convencao 6 se justifica.** Ate esta mudanca o botao
 * apagava direto e o toast oferecia desfazer — mas o desfazer recria a TAG, nao
 * as marcacoes: `task_tags` vai embora no `CASCADE` e o `POST /tags` que o
 * desfazer dispara nao tem como saber quais tarefas carregavam o rotulo. Uma
 * tag em trinta tarefas some com as trinta marcacoes, e o efeito acontece fora
 * da tela, na barra lateral, a um clique de distancia de um "✕".
 *
 * O desfazer continua oferecido depois de confirmar: ele devolve o que da para
 * devolver, e a mensagem daqui e do toast dizem exatamente o que ele nao cobre.
 */
function DialogoDeExclusao({ tag, onFechar }: { tag: Tag | null; onFechar: () => void }) {
  const deleteTag = useDeleteTag();

  if (!tag) return null;

  return (
    <ConfirmDialog
      open
      title={`Excluir a tag “#${tag.name}”?`}
      message="As tarefas continuam, sem esse rótulo. O desfazer recria a tag — não as marcações."
      confirmLabel="Excluir"
      onCancel={onFechar}
      onConfirm={() => {
        deleteTag.mutate(tag.id);
        onFechar();
      }}
    />
  );
}
